import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  ViewEncapsulation,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { RECREATE_ON_MENU_PICK } from '../../routing/recreate-on-param-change.strategy';
import { ListViewStateService } from '../../services/list-view-state.service';
import { hasReportsAccess, UserService } from '../admins/user.service';
import { ArReportsAccessService } from '../ar-reports/services/ar-reports-access.service';
import { TooltipDirective } from '../../directives/tooltip.directive';

interface SidebarItem {
  label: string;
  iconName?: string;
  routerLink: string;
  quickBooksIcon?: boolean;
}

interface SidebarGroup {
  id: string;
  label: string;
  collapsedByDefault?: boolean;
  items: SidebarItem[];
}

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, RouterLink, RouterLinkActive, TooltipDirective],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SidebarComponent {
  private readonly userService = inject(UserService);
  private readonly arReportsAccessService = inject(ArReportsAccessService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly router = inject(Router);
  private readonly listState = inject(ListViewStateService);
  // Same grant the route guard and the backend check use, so the three cannot disagree.
  private readonly hasArReportsAccess = this.arReportsAccessService.hasAccess;
  private readonly railModeQuery =
    typeof window === 'undefined' || !window.matchMedia ? null : window.matchMedia('(max-width: 1400px)');
  private readonly railMode = signal(this.railModeQuery?.matches === true);

  // The navigation started by the latest menu pick; undefined until its NavigationStart arrives.
  private menuPickNavigationId: number | undefined;

  constructor() {
    this.arReportsAccessService.canAccess().pipe(takeUntilDestroyed()).subscribe();
    // Capture runs before RouterLink's own click handler, so the saved list views are already gone
    // (and saving is paused) when the list being left sees the navigation start.
    const host = this.elementRef.nativeElement;
    const onMenuPick = (event: MouseEvent) => this.onMenuPick(event);
    host.addEventListener('click', onMenuPick, true);
    this.destroyRef.onDestroy(() => host.removeEventListener('click', onMenuPick, true));
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (!this.listState.resetting) return;
      if (event instanceof NavigationStart) {
        this.menuPickNavigationId ??= event.id;
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError ||
        event instanceof NavigationSkipped
      ) {
        // An earlier navigation superseded by this pick settles with its own id and must not resume
        // saving; a skipped pick never starts, so it settles the reset directly.
        const settled =
          this.menuPickNavigationId === undefined
            ? event instanceof NavigationSkipped
            : event.id === this.menuPickNavigationId;
        if (!settled) return;
        if (event instanceof NavigationCancel && event.code === NavigationCancellationCode.Redirect) {
          // A guard sent the pick elsewhere: the reset lasts until that redirect settles, or the
          // list being left would save itself again as the redirect starts.
          this.menuPickNavigationId = undefined;
          return;
        }
        this.listState.endReset();
      }
    });
    const query = this.railModeQuery;
    if (query) {
      const update = (event: MediaQueryListEvent | MediaQueryList) => this.railMode.set(event.matches);
      query.addEventListener('change', update);
      this.destroyRef.onDestroy(() => query.removeEventListener('change', update));
    }
  }

  readonly menuGroups = computed(() => {
    const user = this.userService.currentUser();
    return this.buildMenu(user ? hasReportsAccess(user) : false, this.hasArReportsAccess());
  });
  collapsedGroups = signal<Record<string, boolean>>(this.readCollapsedGroups());
  private readonly sidebarHovered = signal(false);
  private readonly sidebarKeyboardFocused = signal(false);
  private readonly sidebarClicked = signal(false);
  readonly sidebarExpanded = computed(
    () => this.sidebarHovered() || this.sidebarKeyboardFocused() || this.sidebarClicked(),
  );

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(event: PointerEvent): void {
    if (event.target instanceof Node && this.elementRef.nativeElement.contains(event.target)) {
      return;
    }

    this.sidebarClicked.set(false);
  }

  /**
   * Picking a page in the menu starts a new task, so every list opens clean afterwards — including
   * the current one. Breadcrumbs, record links and browser Back never come through here, which is
   * what lets them return to a list exactly as it was left. Modified clicks open another tab and
   * leave this one alone.
   */
  private onMenuPick(event: MouseEvent): void {
    const link =
      event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a.sidebar-menu-item') : null;
    if (!link || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    this.menuPickNavigationId = undefined;
    this.listState.beginReset();
    const target = link.getAttribute('href');
    if (!target || target !== this.router.url) return;
    let page = this.router.routerState.snapshot.root;
    while (page.firstChild) page = page.firstChild;
    if (!page.data[RECREATE_ON_MENU_PICK]) return;
    // The router ignores a link to the URL it is already on; reload it so the list is rebuilt.
    event.preventDefault();
    event.stopPropagation();
    void this.router.navigateByUrl(target, { onSameUrlNavigation: 'reload' });
  }

  toggleGroup(groupId: string): void {
    this.collapsedGroups.update((groups) => {
      const next = { ...groups, [groupId]: !this.isGroupCollapsed(groupId) };
      this.writeCollapsedGroups(next);
      return next;
    });
  }

  isGroupCollapsed(groupId: string): boolean {
    const stored = this.collapsedGroups()[groupId];
    if (stored !== undefined) {
      return stored;
    }

    return this.menuGroups().find((group) => group.id === groupId)?.collapsedByDefault ?? false;
  }

  setSidebarExpanded(expanded: boolean): void {
    this.sidebarHovered.set(expanded);
  }

  onSidebarPointerDown(): void {
    this.sidebarClicked.set(this.railMode());
  }

  onSidebarFocusIn(event: FocusEvent): void {
    this.sidebarKeyboardFocused.set(this.hasFocusVisibleDescendant(event.currentTarget));
  }

  onSidebarFocusOut(event: FocusEvent): void {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
      return;
    }

    this.sidebarKeyboardFocused.set(false);
  }

  sidebarItemTooltip(label: string): string {
    return this.railMode() && !this.sidebarExpanded() ? label : '';
  }

  private buildMenu(hasReportsAccess: boolean, hasArReportsAccess = false): SidebarGroup[] {
    return [
      {
        id: 'operations',
        label: 'Operations',
        items: [
          { label: 'Orders', iconName: 'orders', routerLink: '/orders' },
          { label: 'Invoices', iconName: 'invoices', routerLink: '/invoices' },
          { label: 'Payments', iconName: 'invoices', routerLink: '/payments' },
          { label: 'Rentals', iconName: 'rentals', routerLink: '/rentals' },
          { label: 'Rental Machines', iconName: 'rental-machines', routerLink: '/rental-machines' },
          { label: 'Service Slips', iconName: 'service-slips', routerLink: '/service-slips' },
          { label: 'Products', iconName: 'products', routerLink: '/products' },
          { label: 'Pricing Groups', iconName: 'pricing-groups', routerLink: '/pricing-groups' },
          { label: 'Transfers', iconName: 'transfers', routerLink: '/transfers' },
        ],
      },
      {
        id: 'directory',
        label: 'Directory',
        items: [
          { label: 'Customers', iconName: 'customers', routerLink: '/customers' },
          { label: 'Reps', iconName: 'reps', routerLink: '/reps' },
          { label: 'Admins', iconName: 'admins', routerLink: '/admins' },
          { label: 'Territories', iconName: 'territories', routerLink: '/territory-management' },
        ],
      },
      {
        id: 'insights',
        label: 'Insights',
        collapsedByDefault: true,
        // Each Insights entry is gated by its own grant; the group disappears when neither is held.
        items: [
          ...(hasArReportsAccess
            ? [{ label: 'AR Reports', iconName: 'reports', routerLink: '/insights/ar-reports' }]
            : []),
          ...(hasReportsAccess ? [{ label: 'Reports', iconName: 'reports', routerLink: '/reports' }] : []),
        ],
      },
      {
        id: 'settings',
        label: 'Settings',
        collapsedByDefault: true,
        items: [
          { label: 'QuickBooks', routerLink: '/quickbooks', quickBooksIcon: true },
          { label: 'Manual Invoice Sync', iconName: 'manual-invoice-sync', routerLink: '/manual-invoice-sync' },
        ],
      },
    ].filter((group) => group.items.length);
  }

  private hasFocusVisibleDescendant(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }

    try {
      return target.matches(':focus-visible') || target.querySelector(':focus-visible') !== null;
    } catch {
      return false;
    }
  }

  private readCollapsedGroups(): Record<string, boolean> {
    try {
      return JSON.parse(localStorage.getItem('suburban.sidebarGroups.v1') || '{}') as Record<string, boolean>;
    } catch {
      return {};
    }
  }

  private writeCollapsedGroups(groups: Record<string, boolean>): void {
    try {
      localStorage.setItem('suburban.sidebarGroups.v1', JSON.stringify(groups));
    } catch {
      // localStorage can be unavailable in private contexts.
    }
  }
}
